import {
  isReportAttachmentMime,
  MAX_REPORT_ATTACHMENT_BYTES,
  type ReportAttachmentMime,
} from "@bing/shared";

/**
 * PREPARING A REPORT'S EVIDENCE — the pick, the size guard, and the one optimisation we take.
 *
 * A report must carry a screenshot or a screen recording (operator decision, 2026-08-08). This
 * module turns the user's pick into the bytes we upload, and it deliberately does LESS than the
 * avatar path:
 *
 *   • NO DIMENSIONAL DOWNSCALE. A screenshot's value is that a moderator can READ it — the chat
 *     line, the table, the name. Shrinking it destroys the evidence, so the pixels are kept at
 *     native resolution.
 *   • NO CROP. The avatar path hands its pick to `AvatarCropper`; a screenshot wants the whole
 *     frame, so the crop sheet is not in this flow at all.
 *
 * ⚠️ THE ONE THING WE DO DO — PNG → JPEG AT NATIVE RESOLUTION — AND THE MEASURED REASON FOR IT.
 * Phone screenshots are lossless PNG. On 2026-08-08 the upload leg was measured at
 * **0.3% failure for a sub-KB body (n=20,416) and 25.4% for a ~40 KB one (n=59)** — uploads fail far
 * more as they grow, and far more than DOWNLOADS of the same size (a 40 KB asset download that day:
 * 0.00% of 1,051). A phone screenshot is 5–75× larger than the biggest upload this platform has ever
 * measured. Re-encoding to JPEG at the SAME pixel dimensions typically cuts it 3–6× for no loss of
 * legibility, which is the single largest move down that curve available without touching the pixels.
 *
 * ⚠️ AND WE UPLOAD WHICHEVER IS SMALLER. A screenshot of flat UI — a chat thread, a settings page —
 * frequently compresses BETTER as PNG than as JPEG. Shipping the JPEG unconditionally would make
 * those uploads BIGGER, which is precisely backwards. See {@link prepareReportAttachment}.
 */

/** Quality for the re-encode. High enough that text stays crisp; the win comes from the format. */
const JPEG_QUALITY = 0.85;

export type ReportAttachmentErrorCode = "too_large" | "unsupported";

/** A pick we refuse, with a code the sheet turns into a Persian sentence. */
export class ReportAttachmentError extends Error {
  constructor(public readonly code: ReportAttachmentErrorCode) {
    super(code);
    this.name = "ReportAttachmentError";
  }
}

export interface PreparedAttachment {
  /** Exactly what goes on the wire. */
  blob: Blob;
  mime: ReportAttachmentMime;
  /** For the attached-file chip. */
  name: string;
  /** The pick's own size, so the chip can show what the re-encode saved. */
  originalBytes: number;
}

/** `shot.png` → `shot.jpg`, so the moderation group shows a sane filename. */
function jpgName(name: string): string {
  return name.replace(/\.[^.]+$/, "") + ".jpg";
}

/**
 * Decode → redraw at NATIVE size → JPEG. `null` when this device cannot do it at all (an ancient
 * WebView with no `createImageBitmap`/canvas) or refuses these bytes — in both cases the caller
 * uploads the original untouched rather than showing an error, because the re-encode is an
 * OPTIMISATION and must never be able to block a report.
 *
 * ⚠️ THE BITMAP IS CLOSED IN A `finally`. Decoding a 1080×2400 screenshot costs ~10 MB of bitmap;
 * holding several on a LOW-end Android is the memory pressure that used to kill the avatar crop
 * sheet silently. Release it as soon as it has been drawn.
 */
async function reencodeToJpeg(file: Blob): Promise<Blob | null> {
  try {
    if (typeof createImageBitmap !== "function" || typeof document === "undefined") return null;
    const bitmap = await createImageBitmap(file);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width; // native resolution — deliberately NOT scaled
      canvas.height = bitmap.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(bitmap, 0, 0);
      return await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", JPEG_QUALITY);
      });
    } finally {
      (bitmap as { close?: () => void }).close?.();
    }
  } catch {
    return null;
  }
}

/**
 * Everything we can decide from the pick's own metadata — no decode, no `await`, no work at all.
 *
 * ⚠️ THIS IS SEPARATE FOR A REASON, AND THE REASON IS THE UI. The sheet used to render a
 * «در حال آماده‌سازی…» chip WITH A PROGRESS BAR and only then await validation, so a 6 MB pick
 * flashed a bar before being refused — which reads as «it optimised and uploaded my file, and only
 * then told me it was too big» (reported on device, 2026-08-08). Being synchronous is the whole
 * point: the sheet can refuse a bad pick in the same frame, before any chip exists.
 *
 * Order matters: the type check comes first so an unsupported pick says so rather than being
 * refused for its size.
 */
export function reportAttachmentRejection(file: File): ReportAttachmentErrorCode | null {
  if (!isReportAttachmentMime(file.type)) return "unsupported";
  if (file.size > MAX_REPORT_ATTACHMENT_BYTES) return "too_large";
  return null;
}

/**
 * Validate the pick and produce the bytes to upload.
 *
 * The size check runs on the PICK — a 6 MB screenshot is refused up front instead of being decoded
 * (a wasted ~10 MB of bitmap) only to be refused afterwards.
 */
export async function prepareReportAttachment(file: File): Promise<PreparedAttachment> {
  const picked = file.type;
  // ONE source of truth for "may we take this?", shared with the sheet's synchronous guard.
  const rejection = reportAttachmentRejection(file);
  if (rejection) throw new ReportAttachmentError(rejection);
  // The line above has already proven this; repeating the guard is how TypeScript learns it. A cast
  // would compile just as well AND would silently survive the two checks drifting apart.
  if (!isReportAttachmentMime(picked)) throw new ReportAttachmentError("unsupported");

  const base = { name: file.name, originalBytes: file.size };

  // Video rides through untouched: there is no cheap in-browser re-encode, and a screen recording's
  // legibility is the entire reason it was attached.
  if (picked.startsWith("video/")) return { blob: file, mime: picked, ...base };

  const jpeg = await reencodeToJpeg(file);
  if (jpeg && jpeg.size < file.size) {
    return { blob: jpeg, mime: "image/jpeg", name: jpgName(file.name), originalBytes: file.size };
  }
  // Either the device could not re-encode, or the JPEG came out BIGGER (flat UI compresses better
  // as PNG). Send the pick as it is.
  return { blob: file, mime: picked, ...base };
}
